/**
 * Swatch Hover Image Swap
 * Maneja el intercambio de imágenes al hacer hover en swatches
 * Incluye preload inteligente y transiciones suaves
 * Coordina con el sistema secondary image swap para evitar conflictos
 */

class SwatchHover {
  constructor() {
    this.preloadedImages = new Map();
    this.originalImages = new Map();
    this.isTransitioning = new Map();
    this.secondarySwapDisabled = new Map(); // Track which products have secondary swap disabled
    this.debugMode = window.location.search.includes('swatch_debug=true');
    this.init();
  }

  init() {
    // Esperar a que el DOM esté listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initSwatches());
    } else {
      this.initSwatches();
    }
  }

  log(message, data = '') {
    if (this.debugMode) {
      console.log(`🔧 SwatchHover Debug: ${message}`, data);
    }
  }

  initSwatches() {
    const productVariants = document.querySelectorAll('.product-variants');
    console.log('🔍 SwatchHover: Found', productVariants.length, 'product variants containers');
    
    productVariants.forEach(variantContainer => {
      const productId = variantContainer.dataset.productId;
      const productThumbnail = variantContainer.closest('.thumbnail, .product-wrap');
      
      if (!productThumbnail) {
        this.log(`No thumbnail found for product ${productId}`);
        return;
      }
      
      // Marcar el contenedor como activo
      variantContainer.setAttribute('data-swatch-hover-active', 'true');
      
      // Buscar la imagen principal visible con lógica mejorada
      let mainImage = this.findMainImage(productThumbnail, productId);
      
      if (!mainImage) {
        console.log('❌ SwatchHover: No se encontró imagen principal para producto', productId);
        this.log(`All images for product ${productId}:`, productThumbnail.querySelectorAll('img'));
        return;
      }
      
      console.log('✅ SwatchHover: Configurando producto', productId, 'con imagen:', mainImage.src);
      this.log(`Main image found:`, mainImage);

      // Guardar imagen original y estado inicial
      this.originalImages.set(productId, {
        src: mainImage.src,
        alt: mainImage.alt,
        classList: Array.from(mainImage.classList)
      });

      const swatches = variantContainer.querySelectorAll('.variant-swatch');
      this.initSwatchEvents(swatches, mainImage, productId, productThumbnail);
      this.preloadImages(swatches);
      
      // Agregar indicador visual en modo debug
      if (this.debugMode) {
        this.addDebugIndicator(variantContainer, productId);
      }
    });
  }

  addDebugIndicator(container, productId) {
    const indicator = document.createElement('div');
    indicator.innerHTML = `✅ Product ${productId}`;
    indicator.style.cssText = `
      position: absolute;
      top: -20px;
      left: 0;
      background: #4CAF50;
      color: white;
      padding: 2px 6px;
      font-size: 10px;
      border-radius: 3px;
      z-index: 1000;
      pointer-events: none;
    `;
    container.style.position = 'relative';
    container.appendChild(indicator);
  }

  findMainImage(productThumbnail, productId) {
    // Estrategia mejorada para encontrar la imagen principal
    const allImages = productThumbnail.querySelectorAll('.image-element__wrap img, .image__container img');
    this.log(`Finding main image for product ${productId}, found ${allImages.length} images`);
    
    // Primera prioridad: imagen sin clases secondary y visible
    for (let img of allImages) {
      if (!img.classList.contains('secondary') && 
          !img.classList.contains('secondary-media-hidden') &&
          !img.closest('.secondary-media-hidden') &&
          img.offsetParent !== null) { // Verificar que esté visible
        this.log(`Selected main image (priority 1):`, img);
        return img;
      }
    }
    
    // Segunda prioridad: primera imagen disponible que no esté oculta
    for (let img of allImages) {
      if (img.offsetParent !== null) {
        this.log(`Selected main image (priority 2):`, img);
        return img;
      }
    }
    
    // Tercera prioridad: cualquier imagen (fallback)
    const fallbackImage = allImages[0] || null;
    if (fallbackImage) {
      this.log(`Selected main image (fallback):`, fallbackImage);
    }
    return fallbackImage;
  }

  initSwatchEvents(swatches, mainImage, productId, productThumbnail) {
    const hasSecondarySwap = productThumbnail.classList.contains('has-secondary-media-swap');
    this.log(`Product ${productId} has secondary swap: ${hasSecondarySwap}`);
    
    swatches.forEach((swatch, index) => {
      // Agregar indicador de carga
      swatch.addEventListener('mouseenter', (e) => {
        e.preventDefault();
        this.addLoadingState(swatch);
        this.handleSwatchHover(swatch, mainImage, productId, productThumbnail, hasSecondarySwap);
      });

      swatch.addEventListener('touchstart', (e) => {
        e.preventDefault();
        this.addLoadingState(swatch);
        this.handleSwatchHover(swatch, mainImage, productId, productThumbnail, hasSecondarySwap);
      });

      // Agregar número de debug si está activo
      if (this.debugMode) {
        swatch.setAttribute('data-swatch-index', index);
        swatch.title = `${swatch.title} [${index}]`;
      }
    });

    // Reset al salir del contenedor de swatches
    const container = swatches[0]?.closest('.product-variants');
    if (container) {
      container.addEventListener('mouseleave', () => {
        this.resetImage(mainImage, productId, productThumbnail, hasSecondarySwap);
        this.removeLoadingStates(swatches);
      });
    }
  }

  addLoadingState(swatch) {
    swatch.classList.add('loading');
  }

  removeLoadingStates(swatches) {
    swatches.forEach(swatch => {
      swatch.classList.remove('loading');
    });
  }

  handleSwatchHover(swatch, mainImage, productId, productThumbnail, hasSecondarySwap) {
    const featuredImage = swatch.dataset.featuredImage;
    const variantTitle = swatch.dataset.variantTitle;
    
    console.log('🎯 SwatchHover: Hover en swatch', variantTitle, 'imagen:', featuredImage);
    this.log(`Handling hover for swatch:`, { variantTitle, featuredImage, productId });
    
    if (!featuredImage || featuredImage === 'blank') {
      console.log('❌ SwatchHover: No hay imagen featured para este swatch');
      swatch.classList.remove('loading');
      return;
    }
    
    // Evitar múltiples transiciones simultáneas
    if (this.isTransitioning.get(productId)) {
      this.log(`Transition already in progress for product ${productId}`);
      swatch.classList.remove('loading');
      return;
    }
    
    // Desactivar temporalmente el secondary image swap si existe
    if (hasSecondarySwap) {
      this.disableSecondarySwap(productThumbnail, productId);
    }
    
    console.log('🔄 SwatchHover: Iniciando swap de imagen');
    this.swapImage(mainImage, featuredImage, variantTitle, productId, swatch);
  }

  disableSecondarySwap(productThumbnail, productId) {
    if (this.secondarySwapDisabled.get(productId)) return;
    
    // Remover temporalmente los event listeners del secondary swap
    const $productThumbnail = $(productThumbnail);
    if ($productThumbnail.length && window.$ && $productThumbnail.off) {
      $productThumbnail.off('mouseenter.secondarySwap mouseleave.secondarySwap');
      this.secondarySwapDisabled.set(productId, true);
      console.log('🔇 SwatchHover: Secondary swap desactivado para producto', productId);
    }
  }

  enableSecondarySwap(productThumbnail, productId) {
    if (!this.secondarySwapDisabled.get(productId)) return;
    
    // Reactivar el secondary image swap
    if (window.imageFunctions && window.imageFunctions.showSecondaryImage) {
      setTimeout(() => {
        window.imageFunctions.showSecondaryImage();
        this.secondarySwapDisabled.set(productId, false);
        console.log('🔊 SwatchHover: Secondary swap reactivado para producto', productId);
      }, 300); // Pequeño delay para evitar conflictos
    }
  }

  swapImage(mainImage, newSrc, newAlt, productId, swatch) {
    // Verificar si ya tenemos esta imagen preloaded
    if (this.preloadedImages.has(newSrc)) {
      this.performSwap(mainImage, newSrc, newAlt, productId, swatch);
    } else {
      // Preload y swap
      this.loadAndSwap(mainImage, newSrc, newAlt, productId, swatch);
    }
  }

  loadAndSwap(mainImage, newSrc, newAlt, productId, swatch) {
    this.isTransitioning.set(productId, true);
    
    const img = new Image();
    img.onload = () => {
      this.preloadedImages.set(newSrc, true);
      this.performSwap(mainImage, newSrc, newAlt, productId, swatch);
      this.isTransitioning.set(productId, false);
    };
    
    img.onerror = () => {
      console.log('❌ SwatchHover: Error cargando imagen', newSrc);
      this.isTransitioning.set(productId, false);
      if (swatch) swatch.classList.remove('loading');
    };
    
    img.src = newSrc;
  }

  performSwap(mainImage, newSrc, newAlt, productId, swatch) {
    // Añadir clase para transición
    mainImage.classList.add('swapping');
    
    console.log('🔄 SwatchHover: Cambiando imagen de', mainImage.src, 'a', newSrc);
    this.log(`Performing image swap:`, { from: mainImage.src, to: newSrc, productId });
    
    // Cambiar imagen después de un pequeño delay para suavizar
    setTimeout(() => {
      // Para imágenes lazyload, cambiar también data-src
      if (mainImage.hasAttribute('data-src')) {
        mainImage.setAttribute('data-src', newSrc);
      }
      
      // Cambiar src principal
      mainImage.src = newSrc;
      mainImage.alt = newAlt;
      
      // Si tiene srcset, actualizarlo también
      if (mainImage.hasAttribute('srcset') && mainImage.srcset !== '') {
        mainImage.srcset = newSrc;
      }
      
      // Asegurar que no tenga clases problemáticas
      mainImage.classList.remove('secondary-media-hidden', 'secondary');
      
      // Eliminar clases de blur y lazy loading que pueden causar problemas
      mainImage.classList.remove('transition--blur-up', 'lazyload', 'lazyloading');
      
      // Asegurar que tenga la clase lazyloaded para evitar blur
      mainImage.classList.add('lazyloaded');
      
      // Aplicar cleanup completo de blur
      this.cleanupBlur(mainImage);
      
      mainImage.classList.remove('swapping');
      mainImage.classList.add('swapped');
      
      // Remover estado de loading
      if (swatch) swatch.classList.remove('loading');
      
      console.log('✅ SwatchHover: Imagen cambiada exitosamente');
    }, 150);
  }

  resetImage(mainImage, productId, productThumbnail, hasSecondarySwap) {
    const original = this.originalImages.get(productId);
    if (!original) return;

    mainImage.classList.add('swapping');
    this.log(`Resetting image for product ${productId}`);
    
    setTimeout(() => {
      mainImage.src = original.src;
      mainImage.alt = original.alt;
      
      // Restaurar clases originales
      mainImage.className = '';
      original.classList.forEach(cls => mainImage.classList.add(cls));
      
      mainImage.classList.remove('swapping', 'swapped');
      
      // Aplicar cleanup de blur también en reset
      this.cleanupBlur(mainImage);
      
      // Reactivar secondary swap si estaba activo
      if (hasSecondarySwap) {
        this.enableSecondarySwap(productThumbnail, productId);
      }
      
      console.log('🔄 SwatchHover: Imagen restaurada para producto', productId);
    }, 150);
  }

  preloadImages(swatches) {
    // Preload inteligente: solo imágenes visibles en viewport
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.preloadSwatchImages(entry.target);
          observer.unobserve(entry.target);
        }
      });
    });

    swatches.forEach(swatch => {
      observer.observe(swatch);
    });
  }

  preloadSwatchImages(swatch) {
    const featuredImage = swatch.dataset.featuredImage;
    if (featuredImage && featuredImage !== 'blank' && !this.preloadedImages.has(featuredImage)) {
      const img = new Image();
      img.onload = () => {
        this.preloadedImages.set(featuredImage, true);
        this.log(`Preloaded image: ${featuredImage}`);
      };
      img.src = featuredImage;
    }
  }

  // Función especializada para limpiar blur residual
  cleanupBlur(image) {
    // Eliminar clases de blur y lazy loading problemáticas
    image.classList.remove('transition--blur-up', 'lazyload', 'lazyloading');
    
    // Asegurar que tenga la clase lazyloaded
    image.classList.add('lazyloaded');
    
    // Eliminar filtros CSS que pueden causar blur
    image.style.filter = '';
    image.style.webkitFilter = '';
    image.style.transform = '';
    image.style.webkitTransform = '';
    
    // Forzar backface visibility
    image.style.webkitBackfaceVisibility = 'visible';
    image.style.backfaceVisibility = 'visible';
    
    this.log('Blur cleanup applied to image:', image.src);
  }
}

// Inicializar cuando la página esté lista
new SwatchHover();

// Reinicializar para contenido cargado dinámicamente (load more, etc.)
document.addEventListener('load-more:complete', () => {
  new SwatchHover();
});

// Coordinar con el sistema existente de secondary image swap
document.addEventListener('DOMContentLoaded', () => {
  // Pequeño delay para asegurar que el SwatchHover se inicialice primero
  setTimeout(() => {
    if (window.imageFunctions && window.imageFunctions.showSecondaryImage) {
      // Modificar el showSecondaryImage para que respete los swatches
      const originalShowSecondaryImage = window.imageFunctions.showSecondaryImage;
      window.imageFunctions.showSecondaryImage = function() {
        if (window.PXUTheme.media_queries.large.matches) {
          $('.has-secondary-media-swap').off('mouseenter.secondarySwap mouseleave.secondarySwap').on('mouseenter.secondarySwap', function() {
            // Solo ejecutar si no hay swatches o si no están siendo usados
            const $this = $(this);
            const hasSwatches = $this.find('.product-variants .variant-swatch').length > 0;
            
            if (!hasSwatches) {
              // Ejecutar comportamiento original
              if (window.PXUTheme.theme_settings.product_form_style == 'select') {
                $this.find('.product_gallery').toggleClass('secondary-media-hidden');
              }
              $this.find('.image-element__wrap img').toggleClass('secondary-media-hidden');
              $this.find('[data-html5-video]').toggleClass('secondary-media-hidden');
              $this.find('.external-video__container').toggleClass('secondary-media-hidden');
              if (window.videoFeature) window.videoFeature.enableVideoOnHover($this);
            }
          });

          $('.has-secondary-media-swap').on('mouseleave.secondarySwap', function() {
            const $this = $(this);
            const hasSwatches = $this.find('.product-variants .variant-swatch').length > 0;
            
            if (!hasSwatches) {
              // Ejecutar comportamiento original
              if (window.PXUTheme.theme_settings.product_form_style == 'select') {
                $this.find('.product_gallery').toggleClass('secondary-media-hidden');
              }
              $this.find('.image-element__wrap img').toggleClass('secondary-media-hidden');
              $this.find('[data-html5-video]').toggleClass('secondary-media-hidden');
              $this.find('.external-video__container').toggleClass('secondary-media-hidden');
              if (window.videoFeature) window.videoFeature.enableVideoOnHover($this);
            }
          });
        }
      };
      
      // Reinicializar con la nueva lógica
      window.imageFunctions.showSecondaryImage();
      console.log('🔧 SwatchHover: Sistema secondary image swap modificado para coordinación');
    }
  }, 100);
}); 